/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { Button, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';

import { ICittaAccountSubscriptionData } from './citta-account-subscription.data';
import { ICittaAccountSubscriptionProps } from './citta-account-subscription.props.autogenerated';

import { IDXC_SubscribeNewsletterRealtimeResponse } from '../../data-actions/generated/DataServiceEntities.g';
import { subscribeEmailAction, SubscribeEmailDataActionInput } from './../../data-actions/subscribe-email/subscribe-email-data';

export interface ICittaAccountSubscriptionViewProps extends ICittaAccountSubscriptionProps<ICittaAccountSubscriptionData> {
    hasAccountDetails: boolean;
    isProcessing: boolean;
    subUnsubButtton: React.ReactNode;
    subscribeNote: React.ReactNode;
    subscribeAlert: React.ReactNode;
    AccountSubscriptionWrapper: IModuleProps;
    subscribeResponse: React.ReactNode;
    AccountSubscriptionAttribute: INodeProps;
    AccountSubscriptionProcessing: INodeProps;
    AccountSubscriptionUnSubscribe: INodeProps;
    processingMessage: React.ReactNode;
}

interface ICittaAccountSubscriptionState {
    alertMessage: string;
    alertClass: string;
    userEmail: string;
    isSubscribed: boolean;
    hasAccountDetails: boolean;
    isProcessing: boolean;
}

/**
 *
 * CittaAccountSubscription component
 * @extends {React.PureComponent<ICittaAccountSubscriptionProps<ICittaAccountSubscriptionData>>}
 */
class CittaAccountSubscription extends React.PureComponent<ICittaAccountSubscriptionProps<ICittaAccountSubscriptionData>, ICittaAccountSubscriptionState> {
    constructor(props: ICittaAccountSubscriptionViewProps) {
        super(props);
        this._handleSubscribeClick = this._handleSubscribeClick.bind(this);
        this._handleUnsubscribeClick = this._handleUnsubscribeClick.bind(this);
        const email = this.props.data && this.props.data.accountInformation && this.props.data.accountInformation.result?.Email;
        const subscribed = this._getSubscriptionState();
        const hasDetails = email !== undefined && subscribed !== undefined;

        this.state = {
            alertMessage: '',
            alertClass: '',
            userEmail: email || '',
            isSubscribed: subscribed || false,
            hasAccountDetails: hasDetails,
            isProcessing: false
        };
    }

    public componentDidUpdate(prevProps: ICittaAccountSubscriptionViewProps): void {
        if (this.props.data !== prevProps.data) {
            const { result } = this.props.data.accountInformation;
            if (result) {
                const email: string = this.props.data.accountInformation.result?.Email || '';
                const subscribed: boolean = this._getSubscriptionState() || false;
                const hasDetails1 = email !== undefined && subscribed !== undefined;
                this.setState({ userEmail: email, isSubscribed: subscribed, hasAccountDetails: hasDetails1 });
            }
        }

    }

    public render(): JSX.Element | null {
        const { hasAccountDetails, isProcessing } = this.state;
        const { config } = this.props;
        const CittaAccountSubscriptionViewProps = {
            ...this.props,
            AccountSubscriptionWrapper: {
                moduleProps: this.props,
                className: classnames('citta-account-subscription', config.className)
            },
            AccountSubscriptionAttribute: {
                className: 'citta-account-has-account'
            },
            AccountSubscriptionProcessing: {
                className: 'citta-account-subscription-processing'
            },
            AccountSubscriptionUnSubscribe: {
                className: 'citta-account-subscription-unsubscription'
            },
            subscribeResponse: this._getSubscribeResponse(),
            hasAccountDetails: hasAccountDetails,
            isProcessing: isProcessing,
            processingMessage: this._renderProcessing(),
            subUnsubButtton: this._renderButtons(),
            subscribeAlert: this._renderSubscribeAlert(),
            subscribeNote: this._renderSubscribeNote()
        };
        return this.props.renderView(CittaAccountSubscriptionViewProps) as React.ReactElement;
    }
    private _getSubscriptionState(): boolean | undefined {
        const accountInfo = this.props.data.accountInformation.result;
        if (accountInfo) {
            const attributes = accountInfo.Attributes;
            if (attributes && attributes.length > 0) {
                let isSubscribed;
                attributes.forEach(attr => {
                    if (attr.Name === this.props.resources.optInAttributeName) {
                        isSubscribed = attr.AttributeValue?.BooleanValue;
                    }
                });
                return isSubscribed;
            }
        }
        return;
    }
    private _getSubscribeResponse(): React.ReactNode {
        const { isSubscribed, hasAccountDetails } = this.state;
        const { resources } = this.props;
        const message = hasAccountDetails
            ? <p className='citta-account-subscription-message'>{resources.subscribtionText} <strong>{isSubscribed ? resources.subscribtionStatus : resources.noSubscribtionStatus}</strong>{resources.subscribtionMailText}</p>
            : <p className='citta-account-subscription-message'>{resources.missingInfoMessage}</p>;

        return (
            <div>
                <h2 className='citta-account-subscription-heading'>{resources.emailHeadingText}</h2>
                {message}
            </div>
        );
    }

    private _renderProcessing(): React.ReactNode {
        const { resources } = this.props;
        return (
            <div className='citta-account-subscription-processing'>
                <p>{resources.processingLabel}</p>
            </div>
        );
    }

    // Render subscribe/unsubscribe button
    private _renderButtons(): React.ReactNode {
        const { resources } = this.props;
        const { isSubscribed, isProcessing } = this.state;
        if (isSubscribed) {
            return (
                <Button
                    className='citta-account-subscription-button'
                    color='link'
                    onClick={this._handleUnsubscribeClick}
                    disabled={isProcessing}
                >{isProcessing ? resources.processingLabel : resources.unsubscribeButtonLabel}
                </Button>
            );
        } else {
            return (
                <Button
                    className='citta-account-subscription-button'
                    color='link'
                    onClick={this._handleSubscribeClick}
                    disabled={isProcessing}
                >{isProcessing ? resources.processingLabel : resources.subscribeButtonLabel}
                </Button>
            );
        }
    }

    // Set-subscription alert message
    private _renderSubscribeAlert(): React.ReactNode {
        const { alertMessage, alertClass } = this.state;
        const displayAlert: boolean = (alertMessage ? alertMessage !== '' : false);
        if (displayAlert) {
            return (
                <div className='citta-account-subscription-alert'>
                    <p className={alertClass}>{alertMessage}</p>
                </div>
            );
        } else {
            return null;
        }
    }

    private _renderSubscribeNote(): React.ReactNode {
        const { resources } = this.props;
        return (
            <div className='citta-account-subscription-note'>
                <p>{resources.pleaseNoteMessage}</p>
            </div>
        );
    }

    private _handleSubscribeClick = async () => {
        await this._handleButtonClick(true);
    }

    private _handleUnsubscribeClick = async () => {
        await this._handleButtonClick(false);
    }

    private _handleButtonClick = async (subscribe: boolean) => {
        const { userEmail } = this.state;
        this.setState({
            isProcessing: true
        });
        // Update subscription state for user
        const response = await this._setSubscriptionState(userEmail, subscribe);
        if (response.Result && response.Message) {
            const isSuccess = this._isSuccess(subscribe, response.Message);
            this.setState({
                alertMessage: `${response.Message}`,
                alertClass: isSuccess ? 'alert-green' : 'alert-yellow',
                isProcessing: false
            });
        } else {
            this.setState({
                alertMessage: 'Error processing request',
                alertClass: 'alert-yellow',
                isProcessing: false
            });
        }
    }

    private async _setSubscriptionState(email: string, subscribed: boolean): Promise<IDXC_SubscribeNewsletterRealtimeResponse> {
        try {
            return await subscribeEmailAction(new SubscribeEmailDataActionInput(email, subscribed), this.props.context.actionContext);
        } catch (e) {
            throw this.props.telemetry.error(`Something went wrong ------${e}`);
        }
    }

    private _isSuccess(subscribe: boolean, message: string): boolean {
        if (subscribe) {
            return message === 'Subscribed successfully.';
        } else {
            return message === 'Unsubscribed successfully.';
        }
    }
}

export default CittaAccountSubscription;
